/**
 * 실행계획 줄의 **자체 값**(self) 계산 — 오브젝트별 집계를 위한 것.
 *
 * 왜 필요한가: `DBMS_XPLAN` 의 `Buffers`(캐시읽기)는 **자식을 포함한 누적**이다.
 * 오브젝트별로 그냥 SUM 하면 같은 읽기를 조상 줄마다 다시 세어 총합이 실제의 몇 배가 된다.
 * 데모로 확인되는 값: `HASH JOIN(98,211) = HASH JOIN(41,022) + TABLE ACCESS FULL(57,189)`.
 *
 * **plan monitor 값에는 이 계산을 적용하지 않는다.** `GV$SQL_PLAN_MONITOR` 의
 * `physical_read_bytes`·`physical_read_requests` 는 **그 줄이 직접 한 일**이라 이미 self 다.
 * 여기에 또 빼면 음수가 나고, 클램프에 걸려 조용히 0 이 된다 — 이 구분이 이 파일의 핵심이다.
 *
 * 트리는 `depth` 로만 복원한다(부모 id 컬럼이 없다). 줄 순서는 실행계획 출력 순서이고,
 * 어떤 줄의 자식은 **그 줄 다음에 나오는, 깊이가 정확히 +1 인 줄들**이다 — 더 얕은 줄을
 * 만나면 형제/삼촌이므로 거기서 끊는다.
 */

/** 줄에서 깊이를 꺼낸다 — 수집 경로마다 담기는 자리가 다르다(extra.depth / depth) */
function depthOf(line) {
    const d = (line && line.extra && line.extra.depth != null) ? line.extra.depth : (line ? line.depth : null);
    const n = Number(d);
    return Number.isFinite(n) ? n : 0;
}

/**
 * 각 줄의 **직계 자식** 인덱스 목록.
 * @param {Array} lines 실행계획 출력 순서대로의 줄 배열
 * @returns {number[][]}
 */
function childIndexes(lines) {
    const rows = Array.isArray(lines) ? lines : [];
    const out = rows.map(() => []);
    for (let i = 0; i < rows.length; i++) {
        const d = depthOf(rows[i]);
        for (let j = i + 1; j < rows.length; j++) {
            const dj = depthOf(rows[j]);
            if (dj <= d) break;          // 형제 또는 더 얕은 줄 — 이 부모의 자손이 끝났다
            if (dj === d + 1) out[i].push(j);
        }
    }
    return out;
}

/**
 * 누적 값에서 자체 값을 뽑는다: `self = 값 − Σ(직계 자식의 값)`.
 *
 * - 값이 없는 줄(`null`)은 **0 으로 치지 않고** `null` 로 남긴다 — "읽기가 없었다" 와
 *   "잴 수 없었다" 는 다른 말이다. 다만 자식이 `null` 이면 뺄 것이 없으므로 0 으로 친다.
 * - **음수는 0 으로 클램프한다.** PX 계획에서는 조정자 줄의 누적이 자식보다 작게 보이는
 *   경우가 실제로 있다(데모 3번이 그렇다). 음수를 그대로 두면 막대가 반대로 자란다.
 *
 * @param {Array} lines
 * @param {string} field 누적 값이 든 필드 이름
 * @returns {Array<number|null>} 줄 순서대로의 self 값
 */
function selfValues(lines, field) {
    const rows = Array.isArray(lines) ? lines : [];
    const kids = childIndexes(rows);
    return rows.map((row, i) => {
        const v = row ? row[field] : null;
        if (v == null || Number.isNaN(Number(v))) return null;
        const childSum = kids[i].reduce((a, j) => {
            const cv = rows[j] ? rows[j][field] : null;
            return a + (cv == null || Number.isNaN(Number(cv)) ? 0 : Number(cv));
        }, 0);
        return Math.max(0, Number(v) - childSum);
    });
}

/**
 * 오브젝트별 합계. 막대그래프 한 장이 여기서 나온다.
 *
 * @param {Array} lines
 * @param {object} opt
 * @param {string} opt.field         집계할 필드
 * @param {boolean} opt.cumulative   true 면 self 를 먼저 구한다(xplan Buffers). false 면 값 그대로(plan monitor)
 * @param {string} opt.otherLabel    오브젝트명이 없는 줄을 담을 이름
 * @param {number} [opt.topN]
 * @returns {Array<{name: string, value: number}>} 값 내림차순
 */
function byObject(lines, { field, cumulative, otherLabel, topN = 12 } = {}) {
    const rows = Array.isArray(lines) ? lines : [];
    const vals = cumulative
        ? selfValues(rows, field)
        : rows.map(r => {
            const v = r ? r[field] : null;
            return (v == null || Number.isNaN(Number(v))) ? null : Number(v);
        });

    const acc = new Map();
    rows.forEach((row, i) => {
        const v = vals[i];
        if (v == null || v <= 0) return;     // 0 인 줄은 막대가 없는 것과 같다 — 목록만 길어진다
        // 오브젝트명이 없는 줄(조인·정렬 같은 연산)도 자체 비용이 있다. 버리면 총합이 안 맞는다.
        const name = (row && row.object_name) || (row && row.objectName) || otherLabel;
        acc.set(name, (acc.get(name) || 0) + v);
    });

    return [...acc.entries()]
        .map(([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, topN);
}

export { depthOf, childIndexes, selfValues, byObject };
